import { valibotResolver } from "@hookform/resolvers/valibot";
import {
  ArrowUpRight,
  CaretRight,
  Check,
  PencilSimpleLine,
  X,
} from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { Input } from "common/components/ui/Input";
import { LABEL_COLORS } from "common/constants";
import useAllLabelsMutations from "common/datahooks/useAllLabelsMutations";
import { getSchemaMaxLength, typedObjectKeys } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { LabelSchema, type LabelSchemaType } from "common/schemas";
import type { Label } from "common/types";

import LabelColorPicker from "./LabelColorPicker";
import LabelDropdownMenu from "./LabelDropdownMenu";

import type { LabelType } from "../../types";

type LabelListItemProps = {
  labelType: LabelType;
  label: Label;
  isEditing: boolean;
  onCancelEdit: () => void;
  onStartEdit: () => void;
};

export default function LabelListItem({
  labelType,
  label,
  isEditing,
  onStartEdit,
  onCancelEdit,
}: LabelListItemProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { updateLabel, isUpdatingLabel } = useAllLabelsMutations(labelType);

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<LabelSchemaType>({
      defaultValues: {
        name: label ? label.name : "",
        color: label ? label.color : typedObjectKeys(LABEL_COLORS)[0],
      },
      resolver: valibotResolver(LabelSchema),
    });

  const selectedColor = watch("color");

  function cancelEdit() {
    onCancelEdit();
    reset();
  }

  function onSave(newLabel: LabelSchemaType) {
    updateLabel({ label: newLabel, labelId: label.id }).then(() =>
      onCancelEdit(),
    );
  }

  const isShowingEditLayout = (isEditing || !label) && isDesktop;

  let actionComponent = (
    <Button variant="ghost-black" intent="iconOnly">
      <CaretRight />
    </Button>
  );
  if (isShowingEditLayout) {
    actionComponent = (
      <>
        <Button
          intent="iconOnly"
          size="sm"
          variant="outline-black"
          onClick={cancelEdit}
          disabled={isUpdatingLabel}
        >
          <X />
        </Button>
        <Button
          intent="iconOnly"
          size="sm"
          variant="secondary-purple"
          onClick={handleSubmit(onSave)}
          isLoading={isUpdatingLabel}
          data-cy="submit-edit-label-button"
        >
          <Check />
        </Button>
      </>
    );
  } else if (isDesktop) {
    actionComponent = (
      <>
        <Button
          intent="iconOnly"
          size="sm"
          variant="outline-black"
          onClick={onStartEdit}
          data-cy="edit-label-button"
        >
          <PencilSimpleLine />
        </Button>
        <LabelDropdownMenu
          labelId={label.id}
          editLabel={onStartEdit}
          labelType={labelType}
        />
      </>
    );
  }

  const ContainerComponent = isDesktop ? "div" : "button";

  return (
    <ContainerComponent
      className={clsx(
        "flex items-center gap-x-2 rounded-3xl border border-black-200 p-3",
        isShowingEditLayout && "shadow-sm",
      )}
      onClick={isDesktop ? undefined : onStartEdit}
    >
      {isShowingEditLayout && isDesktop ? (
        <LabelColorPicker
          selectedColor={selectedColor}
          setSelectedColor={(color) => setValue("color", color)}
          variant="picker"
        />
      ) : (
        <div
          className={clsx(
            "size-5 shrink-0 rounded",
            LABEL_COLORS[label.color].bgClassName,
          )}
        />
      )}
      {isShowingEditLayout ? (
        <Input
          {...register("name")}
          className="grow"
          placeholder="Label name"
          autoFocus
          variant="sm"
          maxLength={getSchemaMaxLength(LabelSchema.entries.name)}
          data-cy="label-name-input"
        />
      ) : (
        <div className="flex grow items-center gap-x-2 overflow-hidden">
          <span className="truncate text-start text-button-sm">
            {label.name}
          </span>
          <RenderIf condition={!!label.count}>
            <div className="h-4 w-px bg-black-400" />
            <Link
              className="group flex items-center gap-x-0.5 text-nowrap text-xs text-black-400 hover:text-black-600"
              to={`../../../leads?labels=${encodeURIComponent(JSON.stringify([{ id: label.id, name: label.name }]))}`}
            >
              Used for {label.count} leads
              <ArrowUpRight className="size-4 group-hover:visible lg:invisible" />
            </Link>
          </RenderIf>
        </div>
      )}
      {actionComponent}
    </ContainerComponent>
  );
}
